import { ArrowsClockwiseIcon } from '@phosphor-icons/react';

import { t } from '@web/i18n';
import { AppIcons, useOrgContext } from '@web/ui/core';
import { Button } from '@web/ui/core/atoms';
import { Breadcrumb } from '@web/ui/core/molecules';
import { AppBar } from '@web/ui/core/organisms';

import { useRetriage } from '../hooks/use_retriage';

export function TriageTopBar({ total }: Readonly<{ total: number }>): React.JSX.Element {
  return (
    <AppBar
      leading={<Breadcrumb items={[{ icon: 'tray', label: t('triage.crumb.current') }]} />}
      trailing={
        <div className="inline-flex items-center gap-3 font-mono text-tiny uppercase tracking-eyebrow text-muted-foreground">
          {total > 0 ? <RetriageButton /> : null}
          <span className="inline-flex items-center gap-1.5">
            <AppIcons name="tray" size={12} />
            <span
              data-testid="triage-stat-cards"
              className="tabular-nums font-medium text-foreground"
            >
              {total}
            </span>
            <span>{t('triage.stat.cards', { count: total })}</span>
          </span>
        </div>
      }
    />
  );
}

/**
 * Bulk "kick" of the automatic triage for every card sitting in the inbox —
 * the unblock path after creating a squad the stuck cards should now match.
 * Progress is live: while the consumer drains the box, the button spins and
 * the count beside it falls; a summary toast closes the loop (see
 * `useRetriage`).
 */
function RetriageButton(): React.JSX.Element {
  const { orgId } = useOrgContext();
  const { retriage, isPending, isRunning } = useRetriage(orgId);
  const busy = isPending || isRunning;
  return (
    <Button variant="outline" size="sm" disabled={busy} onClick={retriage}>
      <ArrowsClockwiseIcon
        size={14}
        weight="bold"
        aria-hidden
        className={busy ? 'animate-spin' : undefined}
      />
      {busy ? t('triage.retriage.running') : t('triage.retriage.button')}
    </Button>
  );
}
